import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { X, Check, ChevronDown } from "lucide-react";
import { Command as Cmd } from "cmdk";
import { cn } from "../../../lib/utils";
import { MultiSelectOption } from "../../customs/multi-select";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";

interface EventStatusFilterProps {
  filterState: {
    filterKey: string;
    filter: any;
    setFilter: (filters: any) => void;
  };
  title: string;
  description?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  showTrigger?: boolean;
}

const PopoverEventStatusFilterComponent: React.FC<EventStatusFilterProps> = ({
  filterState,
  title,
  description,
  isOpen,
  onOpenChange,
  showTrigger = true,
}) => {
  const { t } = useTranslation();

  // Get current status value - status is always string[]
  const currentStatus = useMemo(
    () =>
      Array.isArray(filterState.filter[filterState.filterKey])
        ? filterState.filter[filterState.filterKey]
        : [],
    [filterState.filter, filterState.filterKey],
  );

  const [localStatus, setLocalStatus] = useState<string[]>(currentStatus);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Update local state when filter changes
  useEffect(() => {
    setLocalStatus(currentStatus);
  }, [currentStatus]);

  // Toggle function - same logic as MultiSelect
  const toggle = (val: string) => {
    const newStatus = localStatus.includes(val)
      ? localStatus.filter((v) => v !== val)
      : [...localStatus, val];

    setLocalStatus(newStatus);
    filterState.setFilter({
      ...filterState.filter,
      [filterState.filterKey]: newStatus,
    });
  };

  const handleClear = () => {
    setLocalStatus([]);
    filterState.setFilter({
      ...filterState.filter,
      [filterState.filterKey]: [],
    });
  };

  const hasActiveFilter = localStatus.length > 0;

  // Event-specific status options with translations
  const statusOptions: MultiSelectOption[] = [
    { value: "upcoming", label: t("event.upcoming") },
    { value: "ongoing", label: t("event.ongoing") },
    { value: "completed", label: t("event.completed") },
  ];

  const selected = statusOptions.filter((o) => localStatus.includes(o.value));
  const maxBadges = 2;
  const extraCount = Math.max(selected.length - maxBadges, 0);

  return (
    <Popover
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <PopoverTrigger asChild>
        {showTrigger ? (
          <img
            className="ml-2 w-3 cursor-pointer"
            src={MagnifyingGlass}
            alt="Search"
          />
        ) : (
          <button
            type="button"
            className="sr-only"
            aria-hidden="true"
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-4"
        align="start"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm uppercase">{title}</h4>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* MultiSelect-like search bar inside PopoverContent */}
          <div className="relative">
            <Button
              type="button"
              variant="outline"
              disabled={false}
              className={cn(
                "w-full rounded-md h-10 items-center justify-between overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700",
                selected.length === 0 && "text-gray-500 dark:text-gray-400",
              )}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="flex w-full gap-1 items-center font-semibold">
                <div className="flex flex-nowrap gap-1 overflow-hidden">
                  {selected.length === 0 ? (
                    <span className="truncate text-gray-500 dark:text-gray-400 font-normal">
                      {"Select status..."}
                    </span>
                  ) : (
                    <>
                      {selected.slice(0, maxBadges).map((opt) => (
                        <Badge
                          key={opt.value}
                          variant="secondary"
                          className="shrink-0"
                        >
                          {opt.label}
                        </Badge>
                      ))}
                      {extraCount > 0 && (
                        <Badge
                          variant="secondary"
                          className="shrink-0"
                        >
                          +{extraCount}
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>
              {selected.length === 0 && (
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
              )}
            </Button>

            {selected.length > 0 && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }}
                aria-label="Clear"
                title="Clear"
              >
                <X className="h-4 w-4 opacity-60 hover:opacity-100 text-gray-500 dark:text-gray-400" />
              </button>
            )}

            {/* Dropdown list - same as MultiSelect content */}
            {isDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
                <Cmd className="rounded-lg">
                  <Cmd.Input
                    placeholder={t("common.search") || "Search status..."}
                    className="w-full px-3 py-2 text-sm outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 border-b border-gray-200 dark:border-gray-700"
                  />
                  <Cmd.List className="max-h-60 overflow-y-auto p-1">
                    <Cmd.Empty className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      {t("common.no_results") || "No results."}
                    </Cmd.Empty>

                    <Cmd.Group className="overflow-hidden p-1">
                      {statusOptions.map((opt) => {
                        const checked = localStatus.includes(opt.value);
                        return (
                          <Cmd.Item
                            key={opt.value}
                            value={`${opt.value} ${opt.label}`}
                            onSelect={() => {
                              if (!opt.disabled) {
                                toggle(opt.value);
                              }
                            }}
                            className={cn(
                              "flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 aria-selected:bg-gray-100 dark:aria-selected:bg-gray-700",
                              opt.disabled && "opacity-50 pointer-events-none",
                            )}
                          >
                            <span>{opt.label}</span>
                            <Check
                              className={cn(
                                "h-4 w-4 text-gray-600 dark:text-gray-300",
                                checked ? "opacity-100" : "opacity-0",
                              )}
                            />
                          </Cmd.Item>
                        );
                      })}
                    </Cmd.Group>
                  </Cmd.List>
                </Cmd>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PopoverEventStatusFilterComponent;
